import type { Omitt } from "@/types"
import { defineZustandStore } from "@/zustand-utils"
import { Alert, AlertTitle, Snackbar } from "@mui/material"
import { memo } from "react"
import { useStore } from "zustand"

export type Toast = {
  id: string
  title: string
  message: string
  severity: "success" | "error" | "info" | "warning"
}

type ToastStoreState = {
  toasts: Toast[]
}

const { store: toastStore, set } = defineZustandStore<ToastStoreState>({
  initialState: {
    toasts: [],
  },
  storeName: "toastStore",
})

const severityToTitle: Record<Toast["severity"], string> = {
  success: "Success",
  error: "Error",
  info: "Info",
  warning: "Warning",
}

const addToast = (toast: Omitt<Toast, "id" | "title"> & { title?: string }) => {
  const title = toast.title ?? severityToTitle[toast.severity]
  const id = crypto.randomUUID()

  const newToast: Toast = {
    id,
    title,
    message: toast.message,
    severity: toast.severity,
  }

  set((state) => {
    state.toasts.push(newToast)
  })
}

const removeToast = (id: Toast["id"]) =>
  set((state) => ({
    toasts: state.toasts.filter((t) => t.id !== id),
  }))

const clearAll = () => set({ toasts: [] })

const useToasts = () => useStore(toastStore, (state) => state.toasts)

const ToastComponent = memo(({ id, title, message, severity }: Toast) => {
  const onClose = () => {
    removeToast(id)
  }

  return (
    <Snackbar autoHideDuration={3000} onClose={onClose} open>
      <Alert variant="filled" severity={severity} sx={{ width: "100%" }} onClose={onClose}>
        <AlertTitle>{title}</AlertTitle>
        {message}
      </Alert>
    </Snackbar>
  )
})

const Toaster = memo(() => {
  const toasts = useToasts()

  return (
    <>
      {toasts.map((toast) => (
        <ToastComponent key={toast.id} {...toast} />
      ))}
    </>
  )
})

type ToastAction = (message: string, title?: string) => void
const defineToastAction =
  (severity: Toast["severity"]): ToastAction =>
  (message, title) =>
    addToast({ message, title, severity })

export const toastActions = {
  info: defineToastAction("info"),
  success: defineToastAction("success"),
  error: defineToastAction("error"),
  warn: defineToastAction("warning"),
  toast: (message: string, args: Partial<Pick<Toast, "severity" | "title">>) =>
    addToast({ message, title: args.title, severity: args.severity ?? "info" }),
  clearAll,
} as const

export default Toaster
